import { apiFetch, publicApiFetch } from "./client";
import { auth } from "../../config/firebase";

export interface UserProfile {
  id: number;
  fbuid: string;
  email: string;
  username: string;
  firstname: string;
  lastname: string;
  role_id: number;
  systemRole?: string | null;
  isSystemAdmin?: boolean;
  isSuperAdmin?: boolean | number | string;
  created_at: string;
  updated_at: string;
  mobile?: string;
  address?: string;
  shirt_size?: string;
  profile_pic?: string;
  is_verified_email?: boolean;
  artist?: Array<{
    artist_id: string;
    role: string;
    artist_name?: string;
    created_at: string;
    is_artist_mapped?: boolean;
    spotify_url?: string;
    distributor?: string;
    youtube_url?: string;
    apple_url?: string;
    instagram_url?: string;
    facebook_url?: string;
    tiktok_url?: string;
    soundcloud_url?: string;
    twitter_url?: string;
    amazon_music_url?: string;
    deezer_url?: string;
    audiomack_url?: string;
    pandora_url?: string;
    tidal_url?: string;
  }>;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  zip_code?: string;
}

// API Response Interface - Updated to match new API structure
export interface CustomerAccount {
  account_id?: string;
  account_number: string;
  account_status: string;
  account_created_at: string | null;
  routing_number?: string;
}

export interface CustomerDetailsResponse {
  user_id: number;
  artist_name: string[];
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  accounts: CustomerAccount[];
}

export interface GetAllCustomerDetailsApiResponse {
  status: boolean;
  msg: string;
  data: CustomerDetailsResponse[];
  pagination?: PaginationInfo;
}

// Flattened interface for table display
export interface CustomerData {
  user_id: number;
  artist_name: string;
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  account_number: string;
  account_status: string;
  account_created_at: string | null;
  routing_number?: string;
  accounts?: CustomerAccount[];
  // Keep customer_id for navigation compatibility
  customer_id?: number;
}

// Detailed Customer Account Details Response
export interface CustomerAccountDetail {
  accountId: string;
  accountType: string;
  accountNumber: string;
  accountStatus: string;
  accountCreatedAt: string | null;
  accountUpdatedAt: string | null;
  routingNumber: string | null;
  balance: string;
  hold: string | null;
  currency: string | null;
}

export interface CustomerAccountDetailsData {
  customerId: string;
  customerType: string;
  actionType: string;
  createdAt: string | null;
  userId: number;
  isArchive: string;
  user: {
    id: number;
    roleId: number;
    lastname: string;
    mobile: string;
    address: string;
    fbuid: string;
    email: string;
    username: string;
    firstname: string;
  };
  artistNames: string[];
  accounts: CustomerAccountDetail[];
}

export interface CustomerAccountDetailsApiResponse {
  status: boolean;
  msg: string;
  data: CustomerAccountDetailsData;
}

interface CreateUserParams {
  fbuid: string;
  email: string;
  username?: string;
  firstname?: string;
  lastname?: string;
  artistname?: string;
  spotifyUrl?: string;
  youtubeUrl?: string;
  appleUrl?: string;
  instagramUrl?: string;
  tiktokUrl?: string;
  distributor?: string;
  mobile?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  zipCode?: string;
  shirtSize?: string;
  dateOfBirth?: string;
  invitationToken?: string;
}

interface ApiResponse<T> {
  data: T;
}

interface TicketCreateRequest {
  name: string;
  email: string;
  subject: string;
  description: string;
}

interface ZendeskTicketResponse {
  ticketId: number;
  ticketUrl: string;
  message: string;
}

interface CommonResponse {
  message: string;
}

interface VerifyCodeResponse {
  verified: boolean;
  message: string;
  verificationSessionToken?: string;
  verificationTokenExpiresAt?: number;
}

export interface SendVerificationCodeResponse {
  message: string;
  delivery?: {
    email: boolean;
    mobile: boolean;
    maskedEmail?: string | null;
    maskedMobile?: string | null;
  };
}

interface UserByUsernameResponse {
  email: string;
  exists: boolean;
}

interface UserByCheckKeyResponse {
  email: string | null;
  username: string | null;
  mobile: string | null;
  exists: boolean;
}

export const updateUserProfile = async ({
  firstName,
  lastName,
  artistName,
  artistId,
  distributor,
  url_link,
  platform_id,
  newPassword,
  verificationCode,
}: {
  firstName?: string;
  lastName?: string;
  artistName?: string;
  artistId?: string;
  distributor?: string;
  url_link?: string;
  platform_id?: number;
  newPassword?: string;
  verificationCode?: string;
}): Promise<UserProfile> => {
  const body = {
    firstName,
    lastName,
    artistName,
    artistId,
    distributor,
    url_link,
    platform_id,
    newPassword,
    verificationCode,
  };
  const response = await apiFetch("/users/update-profile", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return response.data;
};

export const getUserDistributors = async (): Promise<any[]> => {
  const response = await apiFetch("/users/distributors", {
    method: "GET",
  });
  return response.data;
};

export const deleteUserDistributor = async (id: number): Promise<any> => {
  const response = await apiFetch(`/users/distributors/${id}`, {
    method: "DELETE",
  });
  return response.data;
};

export const getUserProfile = async (): Promise<UserProfile> => {
  const response = await apiFetch("/users/me", {
    method: "GET",
  }) as ApiResponse<UserProfile>;
  
  // Store user ID in sessionStorage if available in response
  if (response && response.data && response.data.id) {
    sessionStorage.setItem('userid', String(response.data.id));
  }
  
  // Store artist ID in sessionStorage if available in response
  if (response && response.data && response.data.artist && Array.isArray(response.data.artist) && response.data.artist.length > 0) {
    // Get the first artist's artist_id
    const firstArtist = response.data.artist[0];
    if (firstArtist && firstArtist.artist_id) {
      sessionStorage.setItem('artistid', String(firstArtist.artist_id));
    }
  }
  
  return response.data;
};

export const getUserByUsername = async (username: string): Promise<UserByUsernameResponse> => {
  const response = await publicApiFetch(`/unauth/users/by-username/${username}`, {
    method: "GET",
  });
  return response.data;
};

// export const checkUserAvailability = async (checkkey: string, checkvalue: string): Promise<UserByCheckKeyResponse> => {
//   const response = await publicApiFetch(`/unauth/users/check/${checkkey}/${checkvalue}`, {
//     method: "GET",
//   });
//   return response.data;
// };

export const checkUserAvailabilityForAuth = async (checkkey: string, checkvalue: string, userId?: string): Promise<UserByCheckKeyResponse> => {
  const url = userId 
    ? `/unauth/users/check/${checkkey}/${checkvalue}?userId=${userId}`
    : `/unauth/users/check/${checkkey}/${checkvalue}`;
  
  const response = await publicApiFetch(url, {
    method: "GET",
  });
  return response.data;
};

export const checkUserExists = async (fbuid: string): Promise<boolean> => {
  try {
    const response = await publicApiFetch(`/unauth/users/me`, {
      method: "POST",
      body: JSON.stringify({ uid: fbuid }),
    });
    
    // Store user ID in sessionStorage if available in response
    if (response && response.data && response.data.id) {
      sessionStorage.setItem('userid', String(response.data.id));
    }
    
    // Store artist ID in sessionStorage if available in response
    if (response && response.data && response.data.artist && Array.isArray(response.data.artist) && response.data.artist.length > 0) {
      // Get the first artist's artist_id
      const firstArtist = response.data.artist[0];
      if (firstArtist && firstArtist.artist_id) {
        sessionStorage.setItem('artistid', String(firstArtist.artist_id));
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Error checking if user exists:", error);
    return false;
  }
};

export const createUserIfNotExists = async (userData: CreateUserParams): Promise<UserProfile | null> => {
  try {
    // try{
    //     const userExists = await getUserProfile();
    //     if (userExists) {
    //         // User already exists, no need to create
    //         return null;
    //     }
    // }catch(err){
    //     // Check if this is a "not found" error, which is expected
    //     const errorMessage = err instanceof Error ? err.message : String(err);
    //     if (errorMessage !== "User profile not found"){
    //         throw new Error(errorMessage || "API request failed");
    //     }
    // }
    
    
    // Generate a username if not provided
    const username = userData.username || userData.email;
    
    // Set default values for firstname and lastname if not provided
    const firstname = userData.firstname || userData.username || "";
    
    const lastname = userData.lastname || "";

    // Create the user using the new profile/create endpoint
    const response = await publicApiFetch("/unauth/users/signup", {
      method: "POST",
      body: JSON.stringify({
        fbuid: userData.fbuid,
        email: userData.email,
        username: username,
        firstname,
        lastname,
        spotifyUrl: userData.spotifyUrl,
        youtubeUrl: userData.youtubeUrl,
        appleUrl: userData.appleUrl,
        instagramUrl: userData.instagramUrl,
        tiktokUrl: userData.tiktokUrl,
        distributor: userData.distributor,
        artistname: userData.artistname,
        mobile: userData.mobile,
        address: userData.address,
        country: userData.country,
        state: userData.state,
        city: userData.city,
        street: userData.street,
        zipCode: userData.zipCode,
        shirtSize: userData.shirtSize,
        dateOfBirth: userData.dateOfBirth,
        invitationToken: userData.invitationToken
      }),
    });
    
    const profile = response?.data || response;
    if (profile && response?.account_approval_status) {
      profile.account_approval_status = response.account_approval_status;
    }
    return profile;
  } catch (error: any) {
    console.error("Error creating user:", error);
    // Re-throw the error so it can be handled by the caller
    // This preserves the error message and status code
    throw error;
  }
};

// Interface for UnitCo account details response
export interface UnitCoAccountDetailsResponse {
  success: boolean;
  data: {
    accountExists: boolean;
    userId: number;
    customer?: {
      customerId: string;
      customerType: string;
      isArchive: string;
      createdAt: string;
    };
    accounts?: Array<{
      accountId: string;
      accountNumber: string;
      accountType: string;
      accountStatus: string;
      balance: number;
      hold: number;
      routingNumber: string;
      currency: string;
      accountCreatedAt: string;
      updatedAt: string;
    }>;
  };
}

export interface UnitCoAccountDetailsError {
  error: {
    message: string;
    status: number;
    code: string;
    details: string;
    requiredPermission?: string;
    type?: string;
  };
}

export const getUnitCoAccountDetails = async (userId: string | number, artistId?: number | null): Promise<UnitCoAccountDetailsResponse> => {
  try {
    // Get current artist ID - prefer parameter, fallback to localStorage
    const currentArtistId = artistId !== undefined && artistId !== null 
      ? artistId.toString() 
      : localStorage.getItem('currentArtistId');
    
    const headers: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest', // Prevent CSRF
    };
    
    // Explicitly add X-Artist-Id header if available
    if (currentArtistId) {
      headers['X-Artist-Id'] = currentArtistId;
      console.log('[getUnitCoAccountDetails] Adding X-Artist-Id header:', currentArtistId);
    } else {
      console.warn('[getUnitCoAccountDetails] No X-Artist-Id header - currentArtistId is null');
    }
    
    // apiFetch returns the data directly from the backend
    // Backend returns { success: true, data: {...} }
    const response = await apiFetch(`/unitco/account-details/${userId}`, {
      method: "GET",
      headers,
    });
    
    // Handle both response structures:
    // 1. If apiFetch wraps it: response.data = { success: true, data: {...} }
    // 2. If apiFetch returns directly: response = { success: true, data: {...} }
    const responseData = (response as any).data || response;
    
    // Ensure we have the correct structure
    if (responseData && typeof responseData === 'object' && 'success' in responseData && 'data' in responseData) {
      return responseData as UnitCoAccountDetailsResponse;
    }
    
    // Fallback: wrap it if needed
    return {
      success: true,
      data: responseData as UnitCoAccountDetailsResponse['data']
    };
  } catch (err: any) {
    console.error("Error getting UnitCo account details:", err);
    
    // Re-throw error so caller can handle it
    throw err;
  }
};

const JWT_TOKEN_KEY_PREFIX = 'unit_jwt_token_';
const JWT_EXP_KEY_PREFIX = 'unit_jwt_exp_';

export const getUnitJwtToken = async (
  forceRefresh: boolean = false,
  artistId?: number | null,
  userId?: string | null
): Promise<{ token: string; exp: number }> => {
  try {
    // Resolve uid once: caller may not pass it; use Firebase auth after login/refresh
    const uid = userId ?? auth.currentUser?.uid ?? null;

    // Before calling API: use valid token from localStorage when available
    if (uid && !forceRefresh) {
      const storedToken = localStorage.getItem(`${JWT_TOKEN_KEY_PREFIX}${uid}`);
      const storedExp = localStorage.getItem(`${JWT_EXP_KEY_PREFIX}${uid}`);
      const bufferMs = 5 * 60 * 1000; // 5 min
      if (storedToken && storedExp) {
        const expMs = parseInt(storedExp, 10);
        if (expMs > Date.now() + bufferMs) {
          return { token: storedToken, exp: Math.floor(expMs / 1000) };
        }
      }
    }

    // Add cache-busting query parameter since we can't use cache-control headers
    const cacheBuster = forceRefresh
      ? `_cb=${Date.now()}_${Math.random().toString(36).substr(2, 9)}_refresh`
      : `_cb=${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Get current artist ID - prefer parameter, fallback to localStorage
    const currentArtistId = artistId !== undefined && artistId !== null
      ? artistId.toString()
      : localStorage.getItem('currentArtistId');

    const headers: Record<string, string> = {
      'X-Requested-With': 'XMLHttpRequest', // Prevent CSRF
    };

    // Explicitly add X-Artist-Id header if available
    if (currentArtistId) {
      headers['X-Artist-Id'] = currentArtistId;
      console.log('[getUnitJwtToken] Adding X-Artist-Id header:', currentArtistId);
    } else {
      console.warn('[getUnitJwtToken] No X-Artist-Id header - currentArtistId is null');
    }

    const response = await apiFetch(`/unitco/jwt?${cacheBuster}`, {
      method: "POST",
      headers,
    }) as ApiResponse<{ token: string; exp: number }>;

    // Support both { data: { token, exp } } and { token, exp } response shapes
    const token = response?.data?.token ?? (response as any)?.token;
    const exp = response?.data?.exp ?? (response as any)?.exp;

    if (!token || typeof token !== "string") {
      throw new Error("Failed to get Unit JWT token");
    }

    const expNum = typeof exp === "number" ? exp : 0;
    const expMs = expNum * 1000;
    // Always store when we have a token (uid already resolved above)
    if (uid) {
      // console.error(uid, "from checking", token, `${JWT_TOKEN_KEY_PREFIX}${uid}`, `${JWT_EXP_KEY_PREFIX}${uid}`, "()()()", expMs.toString())
      localStorage.setItem(`${JWT_TOKEN_KEY_PREFIX}${uid}`, token.toString());
      localStorage.setItem(`${JWT_EXP_KEY_PREFIX}${uid}`, expMs.toString());

    }

    return { token, exp: expNum };
  } catch (err: any) {
    console.error("Error getting Unit JWT token:", err);
    
    // Check if it's a database error (from apiFetch)
    if (err?.isDatabaseError || err?.error?.type === 'database_error') {
      // Return empty token - the retry logic will handle it
      return { token: "", exp: 0 };
    }
    
    // Re-throw other errors so they can be handled by the caller
    throw err;
  }
};

export const createTicket = async (ticket: TicketCreateRequest): Promise<ZendeskTicketResponse> => {
  const response = await apiFetch('/zendesk/tickets', {
    method: 'POST',
    body: JSON.stringify(ticket),
  }) as ZendeskTicketResponse;
  return response;
};

export const checkProfileCompletion = (profile: UserProfile): boolean => {
  return !!(profile.artist?.[0]?.distributor);
};

export const updateEmailVerificationStatus = async (): Promise<UserProfile> => {
  const response = await apiFetch("/users/verify-email", {
    method: "POST",
  });
  return response.data;
};

//Send Verification Code API endpoint
export const sendVerificationCode = async (): Promise<SendVerificationCodeResponse> => {
  const response = await apiFetch("/users/send-verification-code", {
    method: "POST",
    body: JSON.stringify({}),
  });
  return response.data;
};

// Verify change-password OTP code without updating password
export const verifyPasswordChangeCode = async (verificationCode: string): Promise<VerifyCodeResponse> => {
  const response = await apiFetch("/users/verify-verification-code", {
    method: "POST",
    body: JSON.stringify({ verificationCode }),
  });
  return response.data;
};

//Update Artist Profile
export const updateArtistProfile = async ({
  username,
  firstname,
  lastname,
  artistname,
  spotifyUrl,
  distributor,
  mobile,
  address,
  country,
  state,
  city,
  street,
  zipCode,
  shirtSize,
  profilePic,
  youtubeUrl,
  appleUrl,
  instagramUrl,
  facebookUrl,
  tiktokUrl,
  soundcloudUrl,
  twitterUrl,
  amazonMusicUrl,
  deezerUrl,
  audiomackUrl,
  pandoraUrl,
  tidalUrl,
  newPassword,
  verificationCode,
  verificationSessionToken,
  skipArtistId = false, // Set to true for confirm-details (account setup, not artist-scoped)
}: {
  username?: string;
  firstname?: string;
  lastname?: string;
  artistname?: string;
  spotifyUrl?: string;
  distributor?: string;
  mobile?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  zipCode?: string;
  shirtSize?: string;
  profilePic?: string;
  youtubeUrl?: string;
  appleUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tiktokUrl?: string;
  soundcloudUrl?: string;
  twitterUrl?: string;
  amazonMusicUrl?: string;
  deezerUrl?: string;
  audiomackUrl?: string;
  pandoraUrl?: string;
  tidalUrl?: string;
  newPassword?: string;
  verificationCode?: string;
  verificationSessionToken?: string;
  skipArtistId?: boolean; // Skip X-Artist-Id header for confirm-details updates
}): Promise<UserProfile> => {
  const response = await apiFetch("/users/update-profile", {
    method: "POST",
    skipArtistId, // Pass skipArtistId option to apiFetch
    body: JSON.stringify({
      username,
      firstname,
      lastname,
      artistname,
      spotifyUrl,
      distributor,
      mobile,
      address,
      country,
      state,
      city,
      street,
      zipCode,
      shirtSize,
      profilePic,
      youtubeUrl,
      appleUrl,
      instagramUrl,
      facebookUrl,
      tiktokUrl,
      soundcloudUrl,
      twitterUrl,
      amazonMusicUrl,
      deezerUrl,
      audiomackUrl,
      pandoraUrl,
      tidalUrl,
      newPassword,
      verificationCode,
      verificationSessionToken,
    }),
  });
  return response.data;
};

export interface GetAllCustomerDetailsResponse {
  data: CustomerDetailsResponse[];
  pagination: PaginationInfo;
}

export const getAllCustomerDetails = async (
  page: number = 1,
  limit: number = 10,
  exportData: boolean = false,
  filters?: { artist_name?: string; account_number?: string; fullName?: string; firstname?: string; lastname?: string; username?: string }
): Promise<GetAllCustomerDetailsResponse> => {
  const exportParam = exportData ? `&export=true` : '';
  
  // Build query parameters for filters
  const filterParams: string[] = [];
  if (filters?.artist_name) {
    filterParams.push(`artist_name=${encodeURIComponent(filters.artist_name)}`);
  }
  if (filters?.account_number) {
    filterParams.push(`account_number=${encodeURIComponent(filters.account_number)}`);
  }
  if (filters?.fullName) {
    filterParams.push(`fullName=${encodeURIComponent(filters.fullName)}`);
  }
  if (filters?.firstname) {
    filterParams.push(`firstname=${encodeURIComponent(filters.firstname)}`);
  }
  if (filters?.lastname) {
    filterParams.push(`lastname=${encodeURIComponent(filters.lastname)}`);
  }
  if (filters?.username) {
    filterParams.push(`username=${encodeURIComponent(filters.username)}`);
  }
  
  const filterQuery = filterParams.length > 0 ? `&${filterParams.join('&')}` : '';
  const url = `/users/getAllCustomerDetials?page=${page}&limit=${limit}${exportParam}${filterQuery}`;
  
  const response = await apiFetch(url, {
    method: "POST",
  }) as GetAllCustomerDetailsApiResponse;
  
  return {
    data: response.data || [],
    pagination: response.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };
};

export const getCustomerAccountDetails = async (userId: number): Promise<CustomerAccountDetailsData> => {
  const response = await apiFetch("/users/getCustomerAccountDetails", {
    method: "POST",
    body: JSON.stringify({ customerid: userId }),
  }) as CustomerAccountDetailsApiResponse;
  
  return response.data;
};

export interface AccountBalanceAccount {
  account_id: string;
  account_number: string;
  account_type: string;
  account_status: string;
  balance: string;
  hold: string | null;
  currency: string | null;
  account_created_at: string | null;
  updated_at: string;
}

export interface AccountBalanceData {
  user_id: number;
  artist_name: string[];
  accounts: AccountBalanceAccount[];
  total_balance: number;
  total_hold: number;
  available_balance: number;
  account_count: number;
}

export interface AccountBalanceResponse {
  success: boolean;
  data: AccountBalanceData;
}

export const getAccountBalance = async (userId: number): Promise<AccountBalanceData> => {
  const response = await apiFetch("/users/getAccountBalance", {
    method: "POST",
    body: JSON.stringify({ userid: userId }),
  }) as AccountBalanceResponse;
  
  return response.data;
};

// User with artists interface
export interface UserWithArtists {
  id: number;
  role_id: number;
  systemRole?: string | null;
  isSystemAdmin?: boolean;
  created_at: string;
  updated_at: string;
  is_verified_email: boolean;
  lastname: string;
  state: string;
  city: string;
  street: string;
  zip_code: string;
  mobile: string;
  address: string;
  shirt_size: string;
  profile_pic: string | null;
  country: string;
  fbuid: string;
  email: string;
  username: string;
  firstname: string;
  artists: Array<{
    user_id: number;
    is_artist_mapped: boolean;
    is_primary: boolean;
    created_at: string;
    updated_at: string;
    id: number;
    youtube_url: string | null;
    apple_url: string | null;
    instagram_url: string | null;
    facebook_url: string | null;
    tiktok_url: string | null;
    soundcloud_url: string | null;
    twitter_url: string | null;
    amazon_music_url: string | null;
    deezer_url: string | null;
    audiomack_url: string | null;
    pandora_url: string | null;
    tidal_url: string | null;
    clickhouse_artist_id: string;
    role: string;
    artist_name: string;
    spotify_url: string;
    distributor: string;
    chartmetric_id?: string | null;
    artist_data_status?: string | null;
  }>;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GetAllUsersResponse {
  data: UserWithArtists[];
  pagination: PaginationInfo;
}

export const getAllUsers = async (
  page: number = 1, 
  limit: number = 10, 
  exportData: boolean = false,
  filters?: { artist_name?: string; email?: string; mobile?: string; firstname?: string; lastname?: string; username?: string; fullName?: string }
): Promise<GetAllUsersResponse> => {
  const exportParam = exportData ? `&export=true` : '';
  
  // Build query parameters for filters
  const filterParams: string[] = [];
  if (filters?.artist_name) {
    filterParams.push(`artist_name=${encodeURIComponent(filters.artist_name)}`);
  }
  if (filters?.email) {
    filterParams.push(`email=${encodeURIComponent(filters.email)}`);
  }
  if (filters?.mobile) {
    filterParams.push(`mobile=${encodeURIComponent(filters.mobile)}`);
  }
  if (filters?.fullName) {
    filterParams.push(`fullName=${encodeURIComponent(filters.fullName)}`);
  }
  if (filters?.firstname) {
    filterParams.push(`firstname=${encodeURIComponent(filters.firstname)}`);
  }
  if (filters?.lastname) {
    filterParams.push(`lastname=${encodeURIComponent(filters.lastname)}`);
  }
  if (filters?.username) {
    filterParams.push(`username=${encodeURIComponent(filters.username)}`);
  }
  
  const filterQuery = filterParams.length > 0 ? `&${filterParams.join('&')}` : '';
  const url = `/admin/usersList?page=${page}&limit=${limit}${exportParam}${filterQuery}`;
  
  const response = await apiFetch(url, {
    method: "GET",
  });
  return {
    data: response.data || [],
    pagination: response.pagination || {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
  };
};

export const getUserDetails = async (userId: string | number): Promise<UserWithArtists> => {
  const response = await apiFetch(`/admin/usersdetails?userid=${userId}`, {
    method: "GET",
  });
  return response.data;
};

export const updateUserByAdmin = async (
  userId: string | number,
  payload: Record<string, unknown>
): Promise<UserWithArtists> => {
  const response = await apiFetch(`/admin/update-user/${userId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return response.data;
};

export const updateUserPasswordByAdmin = async (
  userId: string | number,
  newPassword: string
): Promise<void> => {
  await apiFetch(`/admin/users/${userId}/password`, {
    method: "PUT",
    body: JSON.stringify({ newPassword }),
  });
};

export type SystemAccessRole = "admin" | "user";

export const updateUserSystemRole = async (
  userId: string | number,
  role: SystemAccessRole
): Promise<{ id: number; email: string; systemRole: string | null; isSystemAdmin: boolean }> => {
  const response = await apiFetch(`/admin/users/${userId}/system-role`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  });
  return response.data;
};

export interface AdminTransactionsSummaryTypeItem {
  type: string;
  amount: number;
  percentage: number;
}

export interface AdminTransactionsSummaryTrendItem {
  label: string;
  credits: number;
  debits: number;
}

export interface AdminTransactionsSummaryUserItem {
  userId: number;
  fullName: string;
  artistName: string;
  accountId: string;
  accountNumber: string | null;
  accountStatus?: string | null;
  totalTransactions: number;
  totalCredits: number;
  totalDebits: number;
  netChange: number;
  lastTransactionDate: string | null;
}

export interface AdminTransactionsSummaryResponse {
  range: string;
  asOf: string;
  overview: {
    activeAccounts: number;
    totalTransactions: number;
    totalCredits: number;
    totalDebits: number;
    netChange: number;
  };
  trend: AdminTransactionsSummaryTrendItem[];
  typeBreakdown: AdminTransactionsSummaryTypeItem[];
  users: AdminTransactionsSummaryUserItem[];
  meta: {
    accountsProcessed: number;
    accountFailures: number;
    failures: Array<{ accountId: string; message: string }>;
    rangeStartDate: string | null;
    transactionFetchLimit: number;
  };
}

export const getAdminTransactionsSummary = async (
  range: '7d' | '30d' | '90d' | 'month' | 'all' = '30d'
): Promise<AdminTransactionsSummaryResponse> => {
  const response = await apiFetch(`/admin/transactions/summary?range=${encodeURIComponent(range)}`, {
    method: 'GET',
  });
  return response.data;
};
